import React, {useRef, useState} from 'react';
import { connect } from 'react-redux';
import {withStyles, Tooltip, IconButton, Typography, capitalize} from '@material-ui/core';
import {FindInPage} from '@material-ui/icons';
import { Redirect } from 'react-router-dom';
import { hasPermission, createHistory, hasAtLeastOnePermission } from '../../sc-utilities';
import LabelTemplateList from "./LabelTemplateList";
import {formatProductTypes} from "../../sc-admin/SystemCertificate/utils";
import {inchToMm} from "./LabelTemplateForm";

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    }
});

const LabelTemplate = props => {
    const tableRef = useRef();
    const { classes, columns, translations, permissions } = props;

    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, id: null, redirectPath: '' });
    const redirectHandler = (path, id = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, id: id, redirectPath: path })
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    const visible = columns && columns['LabelTemplate'];
    let tableColumns = [
        {
            title: translations["action"] || "Action", field: 'Action',
            hidden: visible ? visible['Action'] : false,
            cellStyle: { minWidth: 100, maxWidth: 100 },
            filtering: false,
            render: rowData => <>
                {
                    hasPermission(permissions, 'label_template_load') &&
                    <Tooltip title={translations["label_template_details"] || "Label template details"}>
                        <IconButton
                            id={`edit_label_template_${rowData.LabelTemplateID}`}
                            component='div'
                            onClick={() => redirectHandler(`/settings/label-template/edit?LabelTemplateID=${rowData.LabelTemplateID}`)}
                        >
                            <FindInPage />
                        </IconButton>
                    </Tooltip>
                }
            </>
        },
        {
            title: translations['name'] || 'Name', field: 'Name',
            hidden: visible ? visible['Name'] : false,
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Name ? rowData.Name : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations['paper_width'] || 'Paper width', field: 'PaperWidth',
            hidden: visible ? visible['PaperWidth'] : false,
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.PaperWidth ? `${rowData.PaperWidth}in (${inchToMm(rowData.PaperWidth)} mm)` : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations['paper_height'] || 'Paper height', field: 'PaperHeight',
            hidden: visible ? visible['PaperHeight'] : false,
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.PaperHeight ? `${rowData.PaperHeight}in (${inchToMm(rowData.PaperHeight)} mm)` : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: capitalize(translations['data'] || 'Data'), field: 'Data',
            hidden: visible ? visible['Data'] : false,
            cellStyle: { maxWidth: "150px", textOverflow: 'ellipsis', overflow: 'hidden' },
            filtering: false,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Data ? rowData.Data : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: capitalize(translations['product_type'] || 'Product type'), field: 'ProductType',
            hidden: visible ? visible['ProductType'] : false,
            lookup: { 'MobileProduct': translations['mobile_product'] || 'Mobile', 'PCProduct': translations['pc_product'] || 'Work', 'StorageProduct': translations['storage_product'] || 'Single disk-drives', 'WatchProduct': translations['watch'] || 'Watch', 'HeadphoneProduct': translations['headphone'] || 'Headphone' },
            defaultFilter: ['MobileProduct', 'PCProduct', 'StorageProduct', 'WatchProduct', 'HeadphoneProduct'],
            filtering: true,
            cellStyle: { maxWidth: "150px" },
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.ProductType ? formatProductTypes(rowData.ProductType, translations) : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: capitalize(translations['format'] || 'Format'), field: 'Format',
            hidden: visible ? visible['Format'] : false,
            filtering: true,
            lookup: { 'zebra': 'Zebra', 'brother': 'Brother', 'dymo': 'Dymo' },
            defaultFilter: ['zebra', 'brother', 'dymo'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Format ? rowData.Format : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["type"] || 'Type', field: 'Type',
            hidden: visible ? visible['Type'] : false,
            filtering: true,
            lookup: { 'system': translations["system"] || 'System', 'custom': translations["custom"] || 'Custom' },
            defaultFilter: ['system', 'custom'],
            render: rowData => {
                return <Typography variant={'caption'}>{!rowData.CustomerID ? translations["system"] || 'System' : translations["custom"] || 'Custom'}</Typography>;
            }
        },
        {
            title: translations["default"] || 'Default', field: 'Default',
            hidden: visible ? visible['Default'] : false,
            filtering: true,
            lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
            defaultFilter: ['0', '1'],
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Default ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
            }
        },
        {
            title: capitalize(translations['customer_name'] || 'Customer name'), field: 'CustomerName',
            hidden: visible ? visible['CustomerName'] : false,
            cellStyle: { maxWidth: "150px" },
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Customer ? rowData.Customer.Name : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: capitalize(translations['purpose'] || 'Purpose'), field: 'Purpose',
            hidden: visible ? visible['Purpose'] : false,
            lookup: { 'erasure': translations['erasure'] || 'Erasure', 'diagnose': translations['diagnose'] || 'Diagnose', 'combined': translations['combined'] || 'Combined' },
            defaultFilter: ['erasure', 'diagnose', 'combined'],
            filtering: true,
            cellStyle: { maxWidth: "150px" },
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Purpose ? capitalize(rowData.Purpose) : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
    ];

    if (!hasAtLeastOnePermission(permissions, ['label_template_load'])) {
        tableColumns.shift();
    }

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'label_template_list') &&
                <LabelTemplateList
                    tableColumns={tableColumns}
                    redirectHandler={redirectHandler}
                    tableRef={tableRef}
                />}
        </div>
    );
};

const mapStateToProps = state => {
    return {
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || []
    };
};

export default connect(
    mapStateToProps,
    null
)(withStyles(styles)(LabelTemplate));
